import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import UnwatchIcon from '@wix/da-ds/pkg/Icons/Unwatch';
import StandardDropdown from '@wix/da-shared-react/pkg/Dropdown/Preset/StandardDropdown';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { PostsType } from '../../../../../../types/posts';

import s from './PostsTypeSwitcher.scss';

interface Props {
  isOwner: boolean;
  value: PostsType;
  onChange: (value: PostsType) => void;
  className?: string;
  counts: {
    [key in PostsType]: number;
  };
}

const BI_MENU_ITEM_VALUES = {
  [PostsType.ALL]: 'all_posts',
  [PostsType.JOURNALS]: 'journals',
  [PostsType.POLLS]: 'polls',
  [PostsType.STATUSES]: 'status',
  [PostsType.DRAFTS]: 'drafts',
};

const PostsTypeSwitcher: React.FC<Props> = ({
  isOwner,
  value,
  onChange,
  className,
  counts = {},
}) => {
  const { t } = useTranslation();
  const profile_mobile_heading = useFlag('profile_mobile_heading');
  /* TODO: Clean up non _zero translations after merging profile_mobile_heading feature */
  const makePostTypeLabel = useCallback(
    (postType: PostsType) => (
      <>
        {t(`pages.posts.types.${postType}_zero`)}
        {counts[postType] && (
          <span className={s['count']}>{counts[postType]}</span>
        )}
      </>
    ),
    [counts, t]
  );

  const items = useMemo(
    () =>
      [PostsType.ALL, PostsType.JOURNALS, PostsType.POLLS, PostsType.STATUSES]
        .map(postType => ({
          value: postType,
          /* i18n grep
            'pages.posts.types.all'
            'pages.posts.types.all_zero'
            'pages.posts.types.journals'
            'pages.posts.types.journals_zero'
            'pages.posts.types.polls'
            'pages.posts.types.polls_zero'
            'pages.posts.types.statuses'
            'pages.posts.types.statuses_zero'
          */
          label: profile_mobile_heading
            ? makePostTypeLabel(postType)
            : t(
                `pages.posts.types.${postType}${
                  counts[postType] ? '' : '_zero'
                }`,
                {
                  count: counts[postType],
                }
              ),
          icon: undefined,
          biData: {
            evid: BiEventType.PROFILE_POST_FEED_DROPDOWN,
            menu_item: BI_MENU_ITEM_VALUES[postType],
          },
        }))
        .concat(
          !isOwner
            ? ([] as any)
            : [
                {
                  value: PostsType.DRAFTS,
                  /* i18n grep
                    'pages.posts.types.drafts'
                    'pages.posts.types.drafts_zero'
                  */
                  label: profile_mobile_heading
                    ? makePostTypeLabel(PostsType.DRAFTS)
                    : t(
                        `pages.posts.types.${PostsType.DRAFTS}${
                          counts[PostsType.DRAFTS] ? '' : '_zero'
                        }`,
                        {
                          count: counts[PostsType.DRAFTS],
                        }
                      ),
                  icon: <UnwatchIcon size={IconSize.SMALL} />,
                  biData: {
                    evid: BiEventType.PROFILE_POST_FEED_DROPDOWN,
                    menu_item: BI_MENU_ITEM_VALUES[PostsType.DRAFTS],
                  },
                },
              ]
        ),
    [t, isOwner, counts, profile_mobile_heading, makePostTypeLabel]
  );

  return (
    <StandardDropdown
      role="menu"
      className={className}
      toggleClassName={cn(profile_mobile_heading && s['toggle'])}
      arrowClassName={cn(profile_mobile_heading && s['arrow'])}
      menuClassName={cn(profile_mobile_heading && s['menu'])}
      popperClassName={s['popper']}
      placement="bottom-start"
      selectedValue={value}
      onSelect={onChange}
      items={items}
    />
  );
};

PostsTypeSwitcher.displayName = 'PostsTypeSwitcher';

export default PostsTypeSwitcher;
